import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Mail, RefreshCw, ExternalLink, Flame, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const badgeColors = {
  purple: "bg-purple-100 text-[#7C3AED]",
  green: "bg-emerald-100 text-emerald-700",
  orange: "bg-orange-100 text-orange-700",
  blue: "bg-blue-100 text-blue-700",
};

const Dashboard = () => {
  const [data, setData] = useState({ recent_videos: [], recommended: [], top_picks: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/dashboard"),
        api.get("/swipe-file"),
      ]);
      setData(d.data);
      setSavedIds(new Set(s.data.saved_ids || []));
    } catch (e) {}
  };

  useEffect(() => { load(); }, []);

  const refreshViral = async () => {
    setRefreshing(true);
    try {
      const { data: viral } = await api.get("/dashboard/viral-videos?refresh=true");
      setData((d) => ({ ...d, top_picks: viral.items }));
    } catch (e) {}
    finally { setRefreshing(false); }
  };

  const toggleSave = async (t) => {
    const id = t.video_id || t.id;
    if (savedIds.has(id)) {
      try {
        await api.delete(`/swipe-file/${id}`);
        setSavedIds((s) => { const n = new Set(s); n.delete(id); return n; });
        toast.success("Removed from swipe file");
      } catch (e) { toast.error("Failed"); }
    } else {
      try {
        await api.post("/swipe-file", {
          video_id: id, title: t.title, channel: t.channel,
          thumbnail: t.thumbnail, embed_url: t.embed_url, watch_url: t.watch_url,
          views_label: t.views_label, why_viral: t.why_viral,
          industry: t.industry, year: t.year,
        });
        setSavedIds((s) => new Set(s).add(id));
        toast.success("Saved to swipe file");
      } catch (e) { toast.error("Failed to save"); }
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Dashboard" subtitle="Your daily mission control." testId="dashboard-header" />

      {/* Hero Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
        <Link to="/video-factory/new" className="op-card p-6 flex items-center gap-5 hover:border-[#7C3AED] transition-colors" data-testid="dashboard-submit-video">
          <div className="w-14 h-14 rounded-xl bg-[#EDE9FE] flex items-center justify-center"><Film className="text-[#7C3AED]" size={26} /></div>
          <div>
            <p className="text-lg font-bold text-gray-900">Submit a Video</p>
            <p className="text-sm text-gray-500">Generate platform-ready videos from a topic.</p>
          </div>
        </Link>
        <Link to="/email" className="op-card p-6 flex items-center gap-5 hover:border-[#7C3AED] transition-colors" data-testid="dashboard-create-email">
          <div className="w-14 h-14 rounded-xl bg-[#EDE9FE] flex items-center justify-center"><Mail className="text-[#7C3AED]" size={26} /></div>
          <div>
            <p className="text-lg font-bold text-gray-900">Create an Email</p>
            <p className="text-sm text-gray-500">AI-written campaigns in seconds.</p>
          </div>
        </Link>
      </div>

      {/* Recommended */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Recommended for You Today</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="dashboard-recommended">
        {data.recommended.map((r) => (
          <div key={r.id} className="op-card p-5">
            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium mb-3 ${badgeColors[r.badge_color] || badgeColors.purple}`}>{r.badge}</span>
            <p className="font-semibold text-gray-900 mb-3 leading-snug">{r.title}</p>
            <p className="text-xs text-gray-500 mb-4">{r.type}</p>
            <button className="op-btn-secondary text-xs py-1.5 w-full justify-center">Try it →</button>
          </div>
        ))}
      </div>

      {/* Top Picks - Real Viral Videos */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Flame className="text-orange-500" size={20} /> Viral Right Now{data.top_picks?.[0]?.industry ? ` · ${data.top_picks[0].industry} +` : ""}</h2>
          <p className="text-sm text-gray-500 mt-0.5">Mixed from your industry + adjacent verticals. Studied by 100M+ marketers.</p>
        </div>
        <button onClick={refreshViral} disabled={refreshing} className="op-btn-secondary text-sm py-2" data-testid="refresh-viral">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      {data.top_picks.length === 0 ? (
        <div className="op-card p-8 text-center mb-10">
          <p className="text-gray-500">Loading viral picks…</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="dashboard-top-picks">
          {data.top_picks.map((t) => {
            const isSaved = savedIds.has(t.video_id || t.id);
            return (
            <div key={t.id} className="op-card overflow-hidden group" data-testid={`viral-${t.id}`}>
              <div className="aspect-video bg-black relative">
                {t.embed_url ? (
                  <iframe
                    src={t.embed_url}
                    className="absolute inset-0 w-full h-full"
                    title={t.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover" />
                )}
                <span className="absolute top-2 left-2 px-2.5 py-1 bg-orange-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider z-10 pointer-events-none">{t.views_label}</span>
                {t.industry && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#7C3AED]/90 text-white text-[10px] font-bold rounded-full uppercase tracking-wider z-10 pointer-events-none">{t.industry}</span>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{t.title}</p>
                <p className="text-xs text-gray-500 mb-2">{t.channel}{t.year ? ` · ${t.year}` : ""}</p>
                {t.why_viral && (
                  <p className="text-xs text-gray-600 italic line-clamp-2 mb-3">"{t.why_viral}"</p>
                )}
                <div className="flex items-center justify-between">
                  <a href={t.watch_url} target="_blank" rel="noreferrer" className="text-xs text-[#7C3AED] font-semibold inline-flex items-center gap-1 hover:underline">
                    Watch on YouTube <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => toggleSave(t)}
                    className={`text-xs font-semibold inline-flex items-center gap-1 hover:underline ${isSaved ? 'text-[#7C3AED]' : 'text-gray-500 hover:text-[#7C3AED]'}`}
                    data-testid={`save-viral-${t.id}`}
                    title={isSaved ? "Remove from swipe file" : "Save to swipe file"}
                  >
                    {isSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                    {isSaved ? "Saved" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Recent Videos */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Your Recent Videos</h2>
      {data.recent_videos.length === 0 ? (
        <div className="op-card">
          <EmptyState icon={Film} title="No videos yet" description="Create your first AI video in under a minute." action={<Link to="/video-factory/new" className="op-btn-primary">Create Video →</Link>} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-recent-videos">
          {data.recent_videos.map((v) => (
            <Link key={v.id} to={`/video-factory/new/${v.id}`} className="op-card overflow-hidden hover:border-[#7C3AED]">
              <div className="aspect-video bg-gray-100 relative">
                {v.thumbnails?.[0] && <img src={v.thumbnails[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate">{v.title || v.topic}</p>
                <p className="text-xs text-gray-500 capitalize">{v.status}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PageContainer>
  );
};
export default Dashboard;
